import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useDispatch } from "react-redux";
import { getAlarmsAndNotificationsUseCase } from "@src/cloud-connect/support/use-cases/get-alarms-notifications-use-case";
import { getMachineIdForDataSourceUseCase } from "@src/cloud-connect/client-configuration/use-cases/get-machine-id-for-data-source-use-case";
import {
  updateAddRequestTechnicalSupportDrawerModal,
  updateAddRequestTechnicalSupportDrawerModalWithoutMachine,
  updateModalsCloseDrawerModal,
} from "@src/reducers/your-reducer";
import { errorSnackbar } from "@src/cloud-connect/snackbars/entities/snackbar-launcher";
import { EventSeverityBand, AlarmNotification } from "@src/types/support";

import severityUrgentIcon from "@src/assets/svg/notification-severity-urgent-red.svg";
import severityWarningIcon from "@src/assets/svg/notification-severity-warning-circle-orange.svg";
import severityInfoIconBlue from "@src/assets/svg/notification-severity-info-blue.svg";
import severityInfoIconRed from "@src/assets/svg/notification-severity-info-red.svg";
import severityInfoIconOrange from "@src/assets/svg/notification-severity-info-orange.svg";
import checkBoxIcon from "@src/assets/svg/checkbox-icon.svg";
import checkedCheckboxIcon from "@src/assets/svg/checked-checkbox-icon.svg";

export type SeverityFilterOption = "all" | EventSeverityBand;

const SEVERITY_ICON_MAP = {
  urgent: [severityUrgentIcon, severityInfoIconRed],
  warning: [severityWarningIcon, severityInfoIconOrange],
  info: [severityInfoIconBlue],
};

export const useAlarmsNotificationsDrawerModalHook = () => {
  const dispatch = useDispatch();

  const [notifications, setNotifications] = useState<AlarmNotification[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [severityFilter, setSeverityFilter] = useState<SeverityFilterOption[]>([
    "all",
  ]);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const filterToggleRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchNotifications = async () => {
      setIsLoading(true);

      const result = await getAlarmsAndNotificationsUseCase();

      if (!result.success) {
        errorSnackbar(result.error);
      } else {
        setNotifications(result.data);
      }

      setIsLoading(false);
    };

    fetchNotifications();
  }, []);

  const getSeverityBand = useCallback(
    (notification: AlarmNotification): EventSeverityBand => {
      if (notification.level === "critical") return "urgent";
      if (notification.level === "warning") return "warning";
      if (notification.level === "info") return "info";

      if (notification.severity > 750) return "urgent";
      if (notification.severity >= 500) return "warning";

      return "info";
    },
    [],
  );

  const getSeverityIcon = (severityBand: EventSeverityBand) =>
    SEVERITY_ICON_MAP[severityBand];

  const toggleSeverityFilter = (value: SeverityFilterOption) => {
    setSeverityFilter((prev) => {
      if (value === "all") return ["all"];

      const filters = prev.filter((f) => f !== "all");

      if (filters.includes(value)) {
        const next = filters.filter((f) => f !== value);
        return next.length ? next : ["all"];
      }

      const next = [...filters, value];
      const allSeverities: EventSeverityBand[] = ["urgent", "warning", "info"];
      const areAllSeveritiesSelected = allSeverities.every((severity) =>
        next.includes(severity),
      );

      return areAllSeveritiesSelected ? ["all"] : next;
    });
  };

  const filteredNotifications = useMemo(() => {
    if (severityFilter.includes("all")) return notifications;

    return notifications.filter((notification) =>
      severityFilter.includes(getSeverityBand(notification)),
    );
  }, [notifications, severityFilter, getSeverityBand]);

  const openSupportDrawer = async (notification: AlarmNotification) => {
    const machineId = notification.data_source_id
      ? await getMachineIdForDataSourceUseCase(notification.data_source_id)
      : undefined;

    const canUseMachineSpecificDrawer = Boolean(
      notification.data_source_name && machineId,
    );

    if (notification.data_source_name && machineId) {
      dispatch(
        updateAddRequestTechnicalSupportDrawerModal({
          machine_id: machineId,
          source_name: notification.data_source_name,
        }),
      );
    } else {
      dispatch(
        updateAddRequestTechnicalSupportDrawerModalWithoutMachine({
          machine_id: machineId,
        }),
      );
    }

    const dynamicAddRequestTechnicalSupportDrawerModalWithMachineOrWithoutMachineId =
      canUseMachineSpecificDrawer
        ? "addRequestTechnicalSupportDrawerModal"
        : "addRequestTechnicalSupportDrawerModalWithoutMachine";

    dispatch(
      updateModalsCloseDrawerModal({
        showModal: false,
        id: "alarmsNotificationsDrawerModal",
      }),
    );

    dispatch(
      updateModalsCloseDrawerModal({
        showModal: true,
        id: dynamicAddRequestTechnicalSupportDrawerModalWithMachineOrWithoutMachineId,
      }),
    );
  };

  const getFilterCheckboxIconByState = (isSelected: boolean) =>
    isSelected ? checkedCheckboxIcon : checkBoxIcon;

  useEffect(() => {
    if (!isFilterOpen) {
      return undefined;
    }

    const handleOutsideClick = (event: MouseEvent) => {
      if (
        filterToggleRef.current &&
        !filterToggleRef.current.contains(event.target as Node)
      ) {
        setIsFilterOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [isFilterOpen]);

  return {
    filteredNotifications,
    getSeverityBand,
    getSeverityIcon,
    isLoading,
    openSupportDrawer,
    severityFilter,
    toggleSeverityFilter,
    getFilterCheckboxIconByState,
    isFilterOpen,
    setIsFilterOpen,
    filterToggleRef,
  };
};
